import { z } from 'zod';
import { addressSchema, partialAddressSchema } from './common.js';
import { dateStringValidation, optionalDateString } from './dateString.js';
export const commonContractTypeSchema = z.enum([
    'Property Management',
    'Insurance',
    'Legal Agreements',
    'Architecture',
    'Engineering',
    'Electrician',
    'Plumbing',
    'Roofing',
    'Siding',
    'Maintenance',
    'Gate/Doorman',
    'Landscaping',
    'Pool',
    'Parking',
    'Security',
    'Pest Control',
    'Other',
]);
export const confidenceSchema = z.number().min(0).max(1);
export const confidenceScoreSchema = z.number().min(0).max(100);
export const contractDocumentRoleSchema = z.enum(['primary', 'amendment', 'supplementary']);
export const documentReferenceSchema = z.object({
    pageNumber: z.coerce.string().optional(),
    paragraphNumber: z.coerce.string().optional(),
    sectionTitle: z.coerce.string().optional(),
    quote: z.coerce.string().optional(),
});
export const missingClauseSchema = z.object({
    clauseType: z.string(),
    importance: z.enum(['required', 'recommended', 'optional']),
    description: z.string(),
    confidence: confidenceSchema,
});
export const recommendationSchema = z.object({
    category: z.string(),
    suggestion: z.string(),
    impact: z.enum(['high', 'medium', 'low']),
    confidence: confidenceSchema,
});
export const contractIssueSchema = z.object({
    type: z.enum(['payment', 'liability', 'termination', 'compliance', 'ambiguity']),
    severity: z.enum(['critical', 'high', 'medium', 'low']),
    description: z.string(),
    confidence: confidenceSchema,
    references: z.array(documentReferenceSchema).default([]),
});
export const contractInsightsSchema = z.object({
    flaggedIssues: z.array(contractIssueSchema),
    missingClauses: z.array(missingClauseSchema),
    recommendations: z.array(recommendationSchema),
});
export const contractScoreReasoningSchema = z.object({
    influencingFactors: z.string(),
    concerningIssues: z.string(),
    strengths: z.string(),
    immediateAttention: z.string(),
});
export const contractScoreSchema = z.object({
    score: confidenceScoreSchema,
    reasoning: contractScoreReasoningSchema,
    breakdown: z.object({
        completeness: confidenceScoreSchema,
        riskLevel: confidenceScoreSchema,
        clarity: confidenceScoreSchema,
        compliance: confidenceScoreSchema,
    }),
});
export const contractStatusSchema = z.enum([
    'Rfp',
    'Proposal',
    'Negotiation',
    'Review',
    'Unverified',
    'Executed',
    'Terminated',
    'Expired',
]);
export const SIGNED_CONTRACT_STATUSES = ['Unverified', 'Executed'];
export const contractTermDerivationStatusSchema = z.enum([
    'running',
    'derived',
    'nothing',
    'failed',
]);
export const contractTypeSchema = z.string().min(1).max(255);
export const rfpVendorSchema = z.object({
    name: z.string().min(1),
    email: z.string().email(),
});
export const rfpRequirementValueTypeSchema = z.enum([
    'Decimal',
    'Integer',
    'Boolean',
    'Date',
    'Time',
    'Duration',
    'Text',
]);
export const rfpRequirementKindSchema = z.enum(['criteria', 'informational']);
export const rfpRequirementSchema = z.object({
    name: z.string().min(1),
    description: z.string().optional(),
    valueType: rfpRequirementValueTypeSchema,
    units: z.string().optional(),
    required: z.boolean().default(true),
    group: z.string().optional(),
    kind: rfpRequirementKindSchema.default('criteria'),
});
export const rfpStateDataSchema = z.object({
    workDescription: z.string().min(1),
    deadline: z.coerce.date().optional(),
    contactName: z.string().min(1),
    contactEmail: z.string().email(),
    contactPhone: z.string().optional(),
    vendors: z.array(rfpVendorSchema).default([]),
    isPublished: z.boolean().default(false),
    publishedAt: z.coerce.date().optional(),
    uploadedDocumentId: z.string().optional(),
    requirements: z.array(rfpRequirementSchema).default([]),
});
export const proposalStateDataSchema = z.record(z.string(), z.unknown());
export const negotiationStateDataSchema = z.record(z.string(), z.unknown());
export const reviewStateDataSchema = z.record(z.string(), z.unknown());
export const contractorInfoSchema = z.object({
    name: z.string().min(1).describe("The contractor's name."),
    address: addressSchema.optional().describe("The contractor's mailing address."),
    email: z.string().email().optional().describe("The contractor's email address."),
    phone: z.string().optional().describe("The contractor's phone number."),
});
export const unverifiedStateDataSchema = z.object({
    vendorName: z.string().optional(),
    contractAmount: z.number().optional(),
    dueDate: optionalDateString,
    terminationDate: optionalDateString,
    contractor: contractorInfoSchema.optional(),
});
export const verifiedExecutedStateDataSchema = z.object({
    vendorName: z.string().min(1),
    contractAmount: z.number(),
    dueDate: dateStringValidation,
    terminationDate: dateStringValidation,
    contractor: contractorInfoSchema.optional(),
});
export const endedContractStateDataSchema = z.object({
    vendorName: z.string().optional(),
    contractAmount: z.number().optional(),
    dueDate: optionalDateString,
    terminationDate: optionalDateString,
    terminatedDate: dateStringValidation,
});
export const terminatedStateDataSchema = endedContractStateDataSchema;
export const expiredStateDataSchema = endedContractStateDataSchema;
export const coordinatesSchema = z.object({
    lat: z.number(),
    lng: z.number(),
});
export const contractSummarySchema = z.object({
    summary: z.string(),
    confidence: confidenceSchema,
});
export const propertyInfoSchema = z.object({
    name: z.string().optional(),
    address: addressSchema.optional(),
});
const llmContractorInfoSchema = z.object({
    name: z.string().min(1),
    address: partialAddressSchema.optional(),
    email: z.string().email().optional(),
    phone: z.string().optional(),
});
const llmPropertyInfoSchema = z.object({
    name: z.string().optional(),
    address: partialAddressSchema.optional(),
});
export const contractPartiesSchema = z.object({
    contractor: llmContractorInfoSchema,
    executedDate: optionalDateString,
    property: llmPropertyInfoSchema,
    confidence: confidenceSchema,
    references: z.array(documentReferenceSchema),
});
export const paymentFrequencySchema = z.enum([
    'one-time',
    'monthly',
    'quarterly',
    'semi-annually',
    'milestone-based',
    'annually',
    'other',
]);
export const currencySchema = z.enum(['USD', 'CAD']);
export const penaltyOrFeeSchema = z.object({
    type: z.string().optional(),
    amount: z.number().optional(),
    percentage: z.number().optional(),
    description: z.string().optional(),
    trigger: z.string().optional(),
});
export const contractPaymentTermsSchema = z.object({
    amount: z.number().optional(),
    currency: currencySchema,
    frequency: paymentFrequencySchema,
    startDate: optionalDateString,
    endDate: optionalDateString,
    description: z.string(),
    terminationPenalty: penaltyOrFeeSchema.optional(),
    lateFees: z.array(penaltyOrFeeSchema).default([]),
    otherCharges: z.array(penaltyOrFeeSchema).default([]),
    confidence: confidenceSchema,
    references: z.array(documentReferenceSchema).default([]),
});
export const termLengthSchema = z.object({
    durationMonths: z.number().optional(),
    startDate: optionalDateString,
    terminationDate: optionalDateString,
    renewalOptions: z.string().optional(),
    autoRenewal: z.boolean().optional(),
});
export const warrantyInfoSchema = z.discriminatedUnion('hasWarranty', [
    z.object({
        hasWarranty: z.literal(false),
    }),
    z.object({
        hasWarranty: z.literal(true),
        description: z.string().min(1),
        durationMonths: z.number().optional(),
    }),
]);
export const insuranceRequirementsSchema = z.discriminatedUnion('required', [
    z.object({
        required: z.literal(false),
    }),
    z.object({
        required: z.literal(true),
        types: z.array(z.string().min(1)).min(1),
        minimumCoverage: z.string().optional(),
        description: z.string().optional(),
    }),
]);
export const contractTermsSchema = z.object({
    scopeOfWork: z.string(),
    termLength: termLengthSchema,
    warranty: warrantyInfoSchema,
    insurance: insuranceRequirementsSchema,
    confidence: confidenceSchema,
    references: z.array(documentReferenceSchema),
});
//# sourceMappingURL=contracts.js.map